import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { HiArrowRight } from 'react-icons/hi';

const InsightsSectionV2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const articles = [
    {
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80',
      category: 'Leadership',
      date: 'December 2025',
      title: 'How to Build High-Performing Teams Without Burnout',
      excerpt: 'Discover the key strategies that allow teams to maintain peak performance while prioritizing well-being.',
      categoryColor: 'from-[#5B8FB9] to-[#7CB98F]',
    },
    {
      image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800&q=80',
      category: 'Well-being',
      date: 'November 2025',
      title: 'The Future of Leadership: Why Well-being Matters',
      excerpt: 'Explore why the most successful organizations are putting employee well-being at the center of their strategy.',
      categoryColor: 'from-[#7CB98F] to-[#D4A5B5]',
    },
    {
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=800&q=80',
      category: 'Management',
      date: 'October 2025',
      title: '5 Leadership Mistakes That Impact Team Performance',
      excerpt: 'Learn about the common pitfalls leaders face and how to avoid them for better team outcomes.',
      categoryColor: 'from-[#D4A5B5] to-[#5B8FB9]',
    },
  ];

  return (
    <section
      id="insights"
      ref={ref}
      data-testid="insights-section-v2"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,rgba(91,143,185,0.03),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(212,165,181,0.03),transparent_50%)]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
        >
          <div>
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#5B8FB9]/10 via-[#7CB98F]/10 to-[#D4A5B5]/10 text-[#5B8FB9] text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6 border border-[#5B8FB9]/20">
              Blog
            </span>
            <h2 
              className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="insights-headline-v2"
            >
              Insights on Leadership<br />& Well-being
            </h2>
          </div>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#5B8FB9] to-[#7CB98F] text-white font-medium rounded-full transition-all duration-300 shadow-md hover:shadow-lg mt-6 md:mt-0" 
            data-testid="insights-view-all-v2"
          >
            View All Articles
            <HiArrowRight className="w-4 h-4" />
          </motion.button>
        </motion.div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {articles.map((article, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -8 }}
              className="group cursor-pointer"
              data-testid={`article-card-v2-${index + 1}`}
            >
              {/* Image with overlay */}
              <div className="aspect-[4/3] rounded-[2rem] overflow-hidden mb-6 relative">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Decorative corner */}
                <div className={`absolute top-4 right-4 w-12 h-12 bg-gradient-to-br ${article.categoryColor} rounded-full opacity-80 group-hover:scale-110 transition-transform duration-300`} />
              </div>

              {/* Meta */}
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 bg-gradient-to-r ${article.categoryColor} text-white text-xs font-bold uppercase tracking-wider rounded-full`}>
                  {article.category}
                </span>
                <span className="text-sm text-gray-500">{article.date}</span>
              </div>

              {/* Title */}
              <h3 
                className="text-xl font-medium text-gray-900 mb-3 group-hover:bg-gradient-to-r group-hover:from-[#5B8FB9] group-hover:to-[#7CB98F] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {article.title}
              </h3>

              {/* Excerpt */}
              <p className="text-gray-600 font-light text-sm leading-relaxed">
                {article.excerpt}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InsightsSectionV2;
